import { useState, useLayoutEffect, useEffect, useRef } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";

import Form from "../../../components/Form/Form";
import Input from "../../../components/Form/Input/Input";

import FilterItem from "./FilterItem";
// import DefaultButton from "../../../components/simple/DefaultButton";

const formatToLocalISO = (date) => {
  if (!date) return null;
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 19);
};

const formatReadableDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);

  // Formats as "Feb 26, 2026"
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const SortOrderWatcher = ({ watch, setSortOrder }) => {
  const watchedSortOrder = watch("sim_sort_filter");

  useEffect(() => {
    if (watchedSortOrder) {
      const valueToSet = watchedSortOrder?.value || watchedSortOrder;
      setSortOrder(valueToSet);
    }
  }, [watchedSortOrder, setSortOrder]);

  return null;
};

const SimTypeWatcher = ({ watch, updateFilters }) => {
  const watchedSimType = watch("sim_types");
  
  // 1. Create a ref to store the actual primitive value
  const previousValueRef = useRef(null);

  useEffect(() => {
    if (watchedSimType) {
      // Extract the primitive string/number
      const valueToSet = watchedSimType?.value || watchedSimType;

      // 2. Only call updateFilters if the value actually changed
      if (previousValueRef.current !== valueToSet) {
        previousValueRef.current = valueToSet; // Update our memory
        updateFilters({ sim_types: valueToSet });
      }
    }
  }, [watchedSimType, updateFilters]);

  return null;
};

const SimFilter = ({
  simType,
  dateRange,
  updateFilters,
  isMobile,
  sortOrder,
  setSortOrder,
  totalResults,
}) => {
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [sortVisible, setSortVisible] = useState(false);

  const [selectedRange, setSelectedRange] = useState();

  const filterRef = useRef(null);

  const clearFilters = () => {
    updateFilters({ date_range_start: "", date_range_end: "" });
    setFiltersVisible(false);
  };

  useLayoutEffect(() => {
    if (totalResults === 0) {
      clearFilters();
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
  }, [totalResults]);

  useEffect(() => {
    if (dateRange) {
      setSelectedRange({
        from: dateRange.dateRangeStart
          ? new Date(dateRange.dateRangeStart)
          : undefined,
        to: dateRange.dateRangeEnd
          ? new Date(dateRange.dateRangeEnd)
          : undefined,
      });
    } else {
      setSelectedRange(undefined);
    }
  }, [dateRange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the ref exists and the click target is NOT inside the ref's element
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFiltersVisible(false);
        setSortVisible(false);
      }
    };

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDateSelect = (range) => {
    setSelectedRange(range);

    // If the user clears the date selection entirely
    if (!range) {
      updateFilters({ date_range_start: "", date_range_end: "" });
      return;
    }

    // Since mode="range", we format both 'from' and 'to' (if they exist)
    const formattedDateRange = {
      date_range_start: formatToLocalISO(range.from),
      date_range_end: formatToLocalISO(range.to),
    };

    updateFilters(formattedDateRange);
  };

  const safeSelectedRange = selectedRange?.from
    ? {
        from: new Date(selectedRange.from),
        to: selectedRange.to ? new Date(selectedRange.to) : undefined,
      }
    : undefined;

  const sortOptions = [
    {
      name: "Default",
      value: "default",
    },
    {
      name: "Score",
      value: "score",
    },
    {
      name: "Completed Date (ASC)",
      value: "completed_asc",
    },
    {
      name: "Completed Date (DESC)",
      value: "completed_desc",
    },
    {
      name: "Sim Duration",
      value: "sim_duration",
    },
  ];

  const typeOptions = [
    {
      name: "Simulation Challenge",
      value: "simulations",
      register: "sim_type_challenge",
    },
    {
      name: "Conversation Insight",
      value: "conversation insights",
      register: "sim_type_insight",
    },
  ];

  console.log({simType})

  return (
    <div className="simulations-filter" ref={filterRef}>
      <div className="core-filters">
        <button
          className={`filter-btn ${filtersVisible ? "active" : ""}`}
          onClick={() => {
            setFiltersVisible(!filtersVisible);
          }}
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.82162 4.72239C2.19133 4.01796 1.87619 3.66574 1.86431 3.3664C1.85398 3.10636 1.96573 2.85643 2.1664 2.69074C2.39741 2.5 2.87003 2.5 3.81527 2.5H16.1844C17.1296 2.5 17.6023 2.5 17.8333 2.69074C18.0339 2.85643 18.1457 3.10636 18.1354 3.3664C18.1235 3.66574 17.8083 4.01796 17.178 4.72239L12.4228 10.037C12.2972 10.1774 12.2344 10.2477 12.1896 10.3276C12.1499 10.3984 12.1207 10.4747 12.1031 10.554C12.0832 10.6435 12.0832 10.7377 12.0832 10.9261V15.382C12.0832 15.5449 12.0832 15.6264 12.0569 15.6969C12.0337 15.7591 11.9959 15.8149 11.9467 15.8596C11.8911 15.9102 11.8154 15.9404 11.6641 16.001L8.83076 17.1343C8.52447 17.2568 8.37133 17.3181 8.24839 17.2925C8.14088 17.2702 8.04654 17.2063 7.98587 17.1148C7.9165 17.0101 7.9165 16.8452 7.9165 16.5153V10.9261C7.9165 10.7377 7.9165 10.6435 7.8966 10.554C7.87895 10.4747 7.8498 10.3984 7.81007 10.3276C7.76528 10.2477 7.70246 10.1774 7.57682 10.037L2.82162 4.72239Z"
              stroke="#FEFEFE"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Filter By
        </button>
        {filtersVisible && (
          <div className="filter-dropdown">
            <div className="filter-heading">
              <strong>Filters</strong>
              <button onClick={() => clearFilters()}>Clear Filters</button>
            </div>
            <FilterItem name="Interaction Type">
              <Form
                action="#"
                options={{
                  defaultValues: {
                    sim_types: simType,
                  },
                }}
              >
                {({ register, formState: { errors }, watch, control }) => {
                  return (
                    <div className="type-filter">
                      <SimTypeWatcher
                        watch={watch}
                        updateFilters={updateFilters}
                      />
                      {Object.keys(typeOptions).map((item, i) => (
                        <Input
                          key={typeOptions[item].value}
                          type="radio"
                          id={typeOptions[item].value}
                          value={typeOptions[item].value}
                          label={typeOptions[item].name}
                          register={register("sim_types")}
                        />
                      ))}
                    </div>
                  );
                }}
              </Form>
            </FilterItem>
            <FilterItem name="Date Range">
              <DayPicker
                mode="range"
                min={1}
                max={31}
                selected={safeSelectedRange}
                onSelect={handleDateSelect}
                defaultMonth={dateRange?.dateRangeStart}
              />
              {dateRange?.dateRangeStart && (
                <div className="date-ranges">
                  <strong>Selected Dates</strong>
                  {formatReadableDate(dateRange.dateRangeStart)}
                  {dateRange?.dateRangeEnd &&
                    ` - ${formatReadableDate(dateRange.dateRangeEnd)}`}
                </div>
              )}
            </FilterItem>
            {/* <FilterItem name="submit" hideName>
              <DefaultButton
                className="apply-filters-btn"
                buttonSize="md"
                wideOnMobile
                type="submit"
              >
                Apply Filters
              </DefaultButton>
            </FilterItem> */}
          </div>
        )}
      </div>
      {isMobile && (
        <>
          <div className="mobile-sort-filters" ref={filterRef}>
            <button
              className={`filter-btn sort ${sortVisible ? "active" : ""}`}
              onClick={() => {
                setSortVisible(!sortVisible);
              }}
            >
              <svg
                width="17"
                height="15"
                viewBox="0 0 17 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.5 0.833496V14.1668M12.5 14.1668L9.16666 10.8335M12.5 14.1668L15.8333 10.8335M4.16666 14.1668V0.833496M4.16666 0.833496L0.833328 4.16683M4.16666 0.833496L7.49999 4.16683"
                  stroke="#FEFEFE"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Sort By
            </button>
            {sortVisible && (
              <div className="filter-dropdown">
                <Form
                  action="#"
                  options={{
                    defaultValues: {
                      sim_sort_filter: sortOrder,
                    },
                  }}
                >
                  {({ register, formState: { errors }, watch, control }) => {
                    return (
                      <>
                        <SortOrderWatcher
                          watch={watch}
                          setSortOrder={setSortOrder}
                        />
                        {Object.keys(sortOptions).map((item, i) => (
                          <Input
                            key={sortOptions[item].value}
                            type="radio"
                            // checked={sortOrder === sortOptions[item].value}
                            id={sortOptions[item].value}
                            value={sortOptions[item].value}
                            label={sortOptions[item].name}
                            register={register("sim_sort_filter")}
                          />
                        ))}
                      </>
                    );
                  }}
                </Form>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SimFilter;
